import * as React from 'react';
import { createContext, useContext, useMemo } from 'react';

import type { AiCoach_RespondToUserMessageInput as AiCoachMessageInput } from '__generated__/graphql-types';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { useLOHPCoachDecisionHesitationTrigger } from 'bundles/front-page/hooks/useLOHPCoachDecisionHesitationTrigger';
import { useLOHPCoachSignupAbandonTrigger } from 'bundles/front-page/hooks/useLOHPCoachSignupAbandonTrigger';
import { useLOHPCoachStorageCleaner } from 'bundles/front-page/hooks/useLOHPCoachStorageCleaner';
import { transformXDPsToCoachFormat } from 'bundles/front-page/utils/coachDataTransformers';
import {
  EXPERIMENT_TYPE,
  type ExperimentType,
  getActiveExperiment,
  setActiveExperiment,
} from 'bundles/front-page/utils/coachStorageUtils';
import {
  shouldGenerateImpressionForAiAssistanceOnLOHPForProductDecision,
  shouldGenerateImpressionForAiAssistanceOnLOHPForSignupAbandonment,
} from 'bundles/front-page/utils/experimentUtils';
import { CoachStateProvider } from 'bundles/unified-common/providers/CoachStateProvider';

import _t from 'i18n!nls/front-page';

const COACH_PRODUCT_DECISION_MODE_ID = 'LOHP_DECISION_HESITATION';
const COACH_PRODUCT_DECISION_ACTION_ID = 'QUESTIONS_ABOUT_THE_PROGRAM';

const COACH_SIGNUP_ABANDONMENT_MODE_ID = 'LOHP_SIGNUP_ABANDONMENT';
const COACH_SIGNUP_ABANDONMENT_ACTION_ID = 'QUESTIONS_ABOUT_SIGNUP';
/**
 * Coach configuration that can be spread directly into ExternalCoachAction
 */
export type CoachConfig = Pick<AiCoachMessageInput, 'config' | 'courseraContext' | 'additionalContext'>;

type ExperimentConfig = {
  coachConfig: CoachConfig;
  promptPillLabel: string;
  sideLabel: React.ReactNode;
  message: string;
};

export type AiAssistanceExperimentContextType = {
  isAiAssistanceEnabled: boolean;
  coachConfig: CoachConfig;
  promptPillLabel: string;
  sideLabel: React.ReactNode;
  message: string;
  isFirstTimeTrigger: boolean;
};

export const AiAssistanceExperimentContext = createContext<AiAssistanceExperimentContextType>({
  isAiAssistanceEnabled: false,
  coachConfig: {
    config: {
      coachModeId: COACH_PRODUCT_DECISION_MODE_ID,
      coachActionId: COACH_PRODUCT_DECISION_ACTION_ID,
    },
    courseraContext: {
      additionalProducts: [],
    },
    additionalContext: [],
  },
  promptPillLabel: '',
  sideLabel: '',
  message: '',
  isFirstTimeTrigger: false,
});

function AiAssistanceExperimentProvider({ children }: React.PropsWithChildren) {
  // Clean up coach storage when the user logs in
  useLOHPCoachStorageCleaner();

  // EXPERIMENT 1: Check if signal conditions are met (LOHP visit + XDP views)
  const {
    shouldShowCoach: shouldTriggerProductDecisionExperiment,
    viewedXDPs,
    isFirstTimeTrigger: isFirstTimeTriggerProductDecision,
  } = useLOHPCoachDecisionHesitationTrigger();

  const coachData = useMemo(() => transformXDPsToCoachFormat(viewedXDPs), [viewedXDPs]);

  const recentProgramName = useMemo(() => {
    return viewedXDPs[0]?.name;
  }, [viewedXDPs]);

  // EXPERIMENT 1: Product Decision experiment config
  const productDecisionExperimentConfig: ExperimentConfig = useMemo(() => {
    return {
      coachConfig: {
        config: {
          coachModeId: COACH_PRODUCT_DECISION_MODE_ID,
          coachActionId: COACH_PRODUCT_DECISION_ACTION_ID,
        },
        courseraContext: coachData.courseraContext,
        additionalContext: coachData.additionalContext,
      },
      sideLabel: recentProgramName ? (
        <FormattedMessage
          message={_t('Not sure about {recentProgramName}?')}
          recentProgramName={<em>{recentProgramName}</em>}
        />
      ) : (
        _t('Not sure about the program?')
      ),
      promptPillLabel: _t("Let's help you decide"),
      message: recentProgramName
        ? _t("I looked at #{programName}, but I'm not sure if it's right for me. Can you help me decide?", {
            programName: recentProgramName,
          })
        : _t("I looked at the program, but I'm not sure if it's right for me. Can you help me decide?"),
    };
  }, [coachData, recentProgramName]);

  // EXPERIMENT 1: Generate EPIC impression and get allocation when signal passes
  const isProductDecisionEnabled = useMemo(() => {
    if (shouldTriggerProductDecisionExperiment) {
      return shouldGenerateImpressionForAiAssistanceOnLOHPForProductDecision();
    }
    return false;
  }, [shouldTriggerProductDecisionExperiment]);

  // EXPERIMENT 2: Check if signal conditions are met (signup abandonment)
  const {
    shouldShowCoach: shouldTriggerSignupAbandonmentExperiment,
    isFirstTimeTrigger: isFirstTimeTriggerSignupAbandonment,
  } = useLOHPCoachSignupAbandonTrigger();

  // EXPERIMENT 2: Generate EPIC impression and get allocation when signal passes
  const isSignupAbandonmentEnabled = useMemo(() => {
    if (shouldTriggerSignupAbandonmentExperiment) {
      return shouldGenerateImpressionForAiAssistanceOnLOHPForSignupAbandonment();
    }
    return false;
  }, [shouldTriggerSignupAbandonmentExperiment]);

  const signupAbandonmentExperimentConfig: ExperimentConfig = useMemo(() => {
    return {
      coachConfig: {
        config: {
          coachModeId: COACH_SIGNUP_ABANDONMENT_MODE_ID,
          coachActionId: COACH_SIGNUP_ABANDONMENT_ACTION_ID,
        },
      },
      sideLabel: _t('Have a quick question before signing up?'),
      promptPillLabel: _t("Let's clear things up"),
      message: _t(
        'I started the registration process, but I still have some questions about signing up. Can you clear things up?'
      ),
    };
  }, []);

  /*
   * We allow the overlaps of the 2 experiments, but we need to ensure that only the first
   * triggered experiment is shown. To do this, we use sessionStorage to persist the first
   * triggered experiment.
   *
   * @returns The active experiment type
   */
  const activeExperiment = useMemo(() => {
    const stored = getActiveExperiment();
    if (stored) {
      return stored;
    }

    let experimentToSet: ExperimentType | null = null;

    if (isSignupAbandonmentEnabled) {
      experimentToSet = EXPERIMENT_TYPE.SIGNUP_ABANDONMENT;
    } else if (isProductDecisionEnabled) {
      experimentToSet = EXPERIMENT_TYPE.PRODUCT_DECISION;
    }

    if (experimentToSet) {
      setActiveExperiment(experimentToSet);
      return experimentToSet;
    }

    return null;
  }, [isSignupAbandonmentEnabled, isProductDecisionEnabled]);

  const isAiAssistanceEnabled = isSignupAbandonmentEnabled || isProductDecisionEnabled;

  // Select active experiment config based on the stored experiment (sticky to first one shown)
  const activeExperimentConfig =
    activeExperiment === EXPERIMENT_TYPE.SIGNUP_ABANDONMENT
      ? signupAbandonmentExperimentConfig
      : productDecisionExperimentConfig;

  // Determine if this is a first-time trigger for the active experiment
  // Only true if no experiment is stored yet, or if the active experiment matches the trigger
  const isFirstTimeTrigger =
    isAiAssistanceEnabled &&
    ((isFirstTimeTriggerProductDecision &&
      (!activeExperiment || activeExperiment === EXPERIMENT_TYPE.PRODUCT_DECISION)) ||
      (isFirstTimeTriggerSignupAbandonment &&
        (!activeExperiment || activeExperiment === EXPERIMENT_TYPE.SIGNUP_ABANDONMENT)));

  return (
    <AiAssistanceExperimentContext.Provider
      value={{
        isAiAssistanceEnabled,
        ...activeExperimentConfig,
        isFirstTimeTrigger,
      }}
    >
      {isAiAssistanceEnabled ? <CoachStateProvider>{children}</CoachStateProvider> : children}
    </AiAssistanceExperimentContext.Provider>
  );
}

export const useAiAssistanceExperimentData = () => {
  return useContext(AiAssistanceExperimentContext);
};

export default AiAssistanceExperimentProvider;
